module Shared.Dto.MapSensorData

open System
open Shared
open Shared.Dto.Dto
open Shared.DtoTypes.LeafletWetness
open Shared.DtoTypes.MapSensorData

type AirSensorData = {
    Date: DateTimeOffset
    AirTemperature: float
    AirHumidity: float
    HumidTemperature: float
    DewPoint: float
    AbsoluteHumidity: float
    MinAirTemperature: DatedValue<float>
    MaxAirTemperature: DatedValue<float>
    Rssi: int option
    BatteryLevel: BatteryLevel option
    LeafletWetness: LeafletWetness
}

type SoilMoistureSection =
    | F1
    | F2
    | F3
    | F4
    | F5
    | F6
    | F7
    | F8
    | F9

type SoilSensorData = {
    Date: DateTimeOffset
    SoilTemperature: float
    SoilMoisture: float
    SoilConductivity: int
    MinSoilMoisture: DatedValue<float>
    MaxSoilHumidity: DatedValue<float>
    Rssi: int option
    BatteryLevel: BatteryLevel option
    MoistureSection: SoilMoistureSection option
}

type RainFallSensorData = {
    Date: DateTimeOffset
    CurrentRainFall: float
    RainFallLastHour: float option
    RainFallLast24h: float option
    Rssi: int option
    BatteryLevel: BatteryLevel option
    TotalRainFallAmount: float
}

type LeafletMoistureSensorData = {
    Date: DateTimeOffset
    Rssi: int option
    BatteryLevel: BatteryLevel option

    LeafletMoisture: float
    LeafletTemperature: float
}

type SoilPhSensorData = {
    Date: DateTimeOffset
    Rssi: int option
    BatteryLevel: BatteryLevel option

    SoilPh: float
    SoilTemperature: float
}

type AverageWindSensorData = {
    Date: DateTimeOffset
    Rssi: int option
    BatteryLevel: BatteryLevel option

    AverageWindSpeed: float
}

type LiquidLevelData = {
    Date: DateTimeOffset
    Rssi: int option
    BatteryLevel: BatteryLevel option

    LiquidLevel: int
}

type SolarRadiationData = {
    Date: DateTimeOffset
    Rssi: int option
    BatteryLevel: BatteryLevel option

    SolarRadiation: int
}

type FourTimesSoilSensorData = {
    Date: DateTimeOffset
    Rssi: int option
    BatteryLevel: BatteryLevel option

    SoilTemperature1: float
    SoilMoisture1: float
    SoilConductivity1: int

    SoilTemperature2: float option
    SoilMoisture2: float option
    SoilConductivity2: int option

    SoilTemperature3: float option
    SoilMoisture3: float option
    SoilConductivity3: int option

    SoilTemperature4: float option
    SoilMoisture4: float option
    SoilConductivity4: int option
}

type DistanceData = {
    Date: DateTimeOffset
    Rssi: int option
    BatteryLevel: BatteryLevel option

    Distance: int
}

type PressureData = {
    Date: DateTimeOffset
    Rssi: int option
    BatteryLevel: BatteryLevel option

    Pressure: float
}

type WaterQualityData = {
    Date: DateTimeOffset
    Rssi: int option
    BatteryLevel: BatteryLevel option

    Turbidity: float option
    DissolvedOxygen: float option
    OxidationReductionPotential: int option
    EC: int option
    PH: float option
}

let windToWindSpeed (wind: Wind) =
    match wind with
    | NoWind -> None
    | Wind data -> Some data.WindSpeed

let windToDirection (wind: Wind) =
    match wind with
    | NoWind -> None
    | Wind data -> Some data.WindDirection


type MapSensorDataList =
    | NoDataAvailable
    | AirData of AirSensorData list
    | GroundData of SoilSensorData list
    | FourTimesSoilData of FourTimesSoilSensorData list
    | RainFallData of RainFallSensorData list
    | LeafletMoistureData of LeafletMoistureSensorData list
    | SoilPhData of SoilPhSensorData list
    | AverageWindSpeed of AverageWindSensorData list
    | WeatherStation of WeatherStationSensorData list
    | LiquidLevel of LiquidLevelData list
    | SolarRadiation of SolarRadiationData list
    | Distance of DistanceData list
    | Pressure of PressureData list
    | WaterQuality of WaterQualityData list

type MapSensorData =
    | NoDataAvailable
    | NoPublicData
    | AirData of AirSensorData
    | SoilData of SoilSensorData
    | FourTimesSoilData of FourTimesSoilSensorData
    | RainFallData of RainFallSensorData
    | LeafletMoistureData of LeafletMoistureSensorData
    | SoilPhData of SoilPhSensorData
    | AverageWindSpeedData of AverageWindSensorData
    | WeatherStationData of WeatherStationSensorData
    | LiquidLevelData of LiquidLevelData
    | SolarRadiationData of SolarRadiationData
    | DistanceData of DistanceData
    | PressureData of PressureData
    | WaterQualityData of WaterQualityData

type MapSensor = {
    Id: int
    Name: string
    SecondName: string option
    Latitude: double
    Longitude: double
    Data: MapSensorData
    DetailsAllowed: bool
}

type MyMapSensorDataDto = {
    Id: int
    Name: string
    SecondName: string option
    Location: Location
    SensorType: MapSensorType
    Data: MapSensorData
}

type UserGroupMapSensorsDto = {
    Name: string
    Senors: MyMapSensorDataDto list
}

type MapSensorDataResponse = {
    MySensors: MyMapSensorDataDto list
    Groups: UserGroupMapSensorsDto list
}

let filterMapSensorDataResponseByType (mapSensorType: MapSensorType) (response: MapSensorDataResponse) = {
    MySensors = response.MySensors |> List.filter (fun x -> x.SensorType = mapSensorType)
    Groups =
        response.Groups
        |> List.map (fun group -> {
            group with
                Senors = group.Senors |> List.filter (fun x -> x.SensorType = mapSensorType)
        })
}

let getRssiOfSensorData (data: MapSensorData) =
    match data with
    | AirData airData -> airData.Rssi
    | SoilData soilData -> soilData.Rssi
    | FourTimesSoilData data -> data.Rssi
    | RainFallData data -> data.Rssi
    | LeafletMoistureData data -> data.Rssi
    | SoilPhData data -> data.Rssi
    | AverageWindSpeedData data -> data.Rssi
    | DistanceData data -> data.Rssi
    | PressureData data -> data.Rssi
    | _ -> None

let getBatteryLevel (data: MapSensorData) =
    match data with
    | AirData airData -> airData.BatteryLevel
    | SoilData soilData -> soilData.BatteryLevel
    | FourTimesSoilData data -> data.BatteryLevel
    | RainFallData data -> data.BatteryLevel
    | LeafletMoistureData data -> data.BatteryLevel
    | SoilPhData data -> data.BatteryLevel
    | AverageWindSpeedData data -> data.BatteryLevel
    | DistanceData data -> data.BatteryLevel
    | PressureData data -> data.BatteryLevel
    | WaterQualityData data -> data.BatteryLevel
    | _ -> None

let getSensorDataDate (data: MapSensorData) =
    match data with
    | NoDataAvailable
    | NoPublicData -> None
    | AirData data -> Some data.Date
    | SoilData data -> Some data.Date
    | FourTimesSoilData data -> Some data.Date
    | RainFallData data -> Some data.Date
    | LeafletMoistureData data -> Some data.Date
    | SoilPhData data -> Some data.Date
    | AverageWindSpeedData data -> Some data.Date
    | WeatherStationData data -> Some data.Date
    | LiquidLevelData data -> Some data.Date
    | SolarRadiationData data -> Some data.Date
    | DistanceData data -> Some data.Date
    | PressureData data -> Some data.Date
    | WaterQualityData data -> Some data.Date

let windDirectionToCompassDirection (degree: float) : CompassDirection =
    if degree >= 337.5 || degree <= 22.5 then
        N
    else if degree > 22.5 && degree < 67.5 then
        NE
    else if degree >= 67.5 && degree <= 112.5 then
        E
    else if degree > 112.5 && degree < 157.5 then
        SE
    else if degree >= 157.5 && degree <= 202.5 then
        S
    else if degree > 202.5 && degree < 247.5 then
        SW
    else if degree >= 247.5 && degree <= 292.5 then
        W
    else if degree > 292.5 && degree < 337.5 then
        NW
    else
        failwithf $"%f{degree} Degrees are not a valid WindDirection"

let compassDirectionToLabel (direction: CompassDirection) : string =
    match direction with
    | N -> "N"
    | NE -> "NO"
    | E -> "O"
    | SE -> "SO"
    | S -> "S"
    | SW -> "SW"
    | W -> "W"
    | NW -> "NW"

let toAirData (data: MapSensorData) : AirSensorData option =
    match data with
    | AirData air -> Some air
    | _ -> None

let toSoilData (data: MapSensorData) : SoilSensorData option =
    match data with
    | SoilData soil -> Some soil
    | _ -> None

let toSoilPhData (data: MapSensorData) : SoilPhSensorData option =
    match data with
    | SoilPhData ph -> Some ph
    | _ -> None

let toRainFallData (data: MapSensorData) : RainFallSensorData option =
    match data with
    | RainFallData rainFall -> Some rainFall
    | _ -> None

let toLeafletMoistureData (data: MapSensorData) : LeafletMoistureSensorData option =
    match data with
    | LeafletMoistureData leafletMoisture -> Some leafletMoisture
    | _ -> None

let toAverageWindSpeedData (data: MapSensorData) : AverageWindSensorData option =
    match data with
    | AverageWindSpeedData averageWindSpeed -> Some averageWindSpeed
    | _ -> None

let toWeatherStationData (data: MapSensorData) : WeatherStationSensorData option =
    match data with
    | WeatherStationData weatherStation -> Some weatherStation
    | _ -> None

let toLiquidLevelData (data: MapSensorData) : LiquidLevelData option =
    match data with
    | LiquidLevelData liquidData -> Some liquidData
    | _ -> None

let toSolarRadiationData (data: MapSensorData) : SolarRadiationData option =
    match data with
    | SolarRadiationData data -> Some data
    | _ -> None

let toFourTimesSoilData (data: MapSensorData) : FourTimesSoilSensorData option =
    match data with
    | FourTimesSoilData soil -> Some soil
    | _ -> None

let toDistanceData (data: MapSensorData) : DistanceData option =
    match data with
    | DistanceData data -> Some data
    | _ -> None

let toPressureData (data: MapSensorData) : PressureData option =
    match data with
    | PressureData liquidData -> Some liquidData
    | _ -> None

let toWaterQualityData (data: MapSensorData) : WaterQualityData option =
    match data with
    | WaterQualityData data -> Some data
    | _ -> None